/**
 * Single source of truth for NSW RLGSP modules and layers.
 *
 * Every per-layer concern lives on that layer's entry in `NSW_RLGSP_MODULES` —
 * map style (a MapLibre layer fragment: type/zoom/layout/paint), z-order,
 * WFS query properties, attributes-panel layout, table tab — and everything
 * downstream is derived from the table:
 *
 * - types: `NswRlgspModule`, `INswRlgspSystemLayers`
 * - `attributesColumns`, `tableTabs`
 * - `buildSystemLayers` in `layers.ts` walks the table to assemble sources
 * - the app legend (`legends/LayerList.svelte` + `legendHelpers.ts`) walks
 *   it to render one checkable row per layer, grouped by module — the single
 *   legend tab itself is wired in `config.ts`
 *
 * To add a layer: drop its style JSON in `layer_styles/` (adding `type` and
 * any zoom bounds alongside the exported `layout`/`paint`; see `LayerEntry`
 * in `#lib/common/moduleTable.js`) and add one entry below. Module and layer keys double as ids; layer keys
 * must match the GeoServer layer titles (the SafeCurves idiom) unless the
 * entry sets `featureTitle` — several entries may render the same root
 * feature with different style filters (they share one vector source).
 */

import type {
	AttributesColumnHelper,
	AttributesLayoutHelper
} from '#lib/components/Attributes.types.js';
import type { ILayerSource } from '#lib/types.js';
import {
	attributesColumnsOf,
	tableTabsOf,
	type LayerEntry,
	type ModuleEntry
} from '#lib/common/moduleTable.js';

import styleCrashes from './layer_styles/road_crash.json';
import styleOperatingSpeed from './layer_styles/operating_speed.json';
import styleInfrastructure from './layer_styles/infrastructure.json';
import styleHistoricSpeeds from './layer_styles/historic_speeds.json';
import styleSpeedZones from './layer_styles/speed_zones.json';
import styleSpeedZonesSchools from './layer_styles/speed_zones_school.json';
import styleRiskAusrap from './layer_styles/risk_ausrap.json';
import styleRoadNetworkState from './layer_styles/road_network_state.json';
import styleRoadNetworkRegional from './layer_styles/road_network_regional.json';
import styleRoadNetworkCycleways from './layer_styles/road_network_cycleways.json';
import styleRoadNetworkBusRoutes from './layer_styles/road_network_bus_routes.json';
import styleRoadNetworkLevelCrossings from './layer_styles/road_network_level_crossings.json';
import stylePoiSchools from './layer_styles/poi_schools.json';
import stylePoiCommunity from './layer_styles/poi_community.json';
import stylePoiRecreation from './layer_styles/poi_recreation.json';
import stylePlanningHeritageFill from './layer_styles/planning_heritage-fill.json';
import stylePlanningHeritageLine from './layer_styles/planning_heritage-line.json';
import stylePlanningZoningFill from './layer_styles/planning_zoning-fill.json';
import stylePlanningZoningLine from './layer_styles/planning_zoning-line.json';
import styleSeifa from './layer_styles/seifa.json';
import styleLgaAreas from './layer_styles/lga_areas.json';
import styleSpeedZoneExtentsSpeed from './layer_styles/speed_zones_extent_speed.json';
import styleSpeedZoneExtentsDoras from './layer_styles/speed_zones_extent_doras.json';

// Attributes `cell` takes the raw value, so only key and header carry over to table columns.
const toColumns = (cols: AttributesColumnHelper[]) =>
	cols.map(({ accessorKey, header }) => ({ accessorKey, header }));

// Attributes-panel layout shared by every crash layer — they all render the
// same GeoServer feature, so a selected crash looks identical whichever
// legend row it was drawn by.
const crashAttributes: AttributesLayoutHelper = {
	title: { accessorKey: 'degree_of_crash___detailed' },
	subheaders: [[{ accessorKey: 'year_of_crash' }, { accessorKey: 'month_of_crash' }]],
	table: [
		{ accessorKey: 'degree_of_crash___detailed', header: () => 'Degree of crash (detailed)' },
		{ accessorKey: 'year_of_crash', header: () => 'Year' },
		{ accessorKey: 'month_of_crash', header: () => 'Month' },
		{ accessorKey: 'type_of_location', header: () => 'Location type' },
		{ accessorKey: 'day_of_week_of_crash', header: () => 'Day of week' },
		{ accessorKey: 'two_hour_intervals', header: () => 'Time (2hr. window)' },
		{ accessorKey: 'no__killed', header: () => 'Killed' },
		{ accessorKey: 'no__seriously_injured', header: () => 'Seriously injured' },
		{ accessorKey: 'no__moderately_injured', header: () => 'Moderately injured' },
		{ accessorKey: 'no__minor_other_injured', header: () => 'Minor/other injured' }
	]
};

// Attributes-panel layouts shared by each planning layer's fill + outline
// entries — both entries render the same GeoServer feature.
const heritageAttributes: AttributesLayoutHelper = {
	title: { accessorKey: 'lay_class' },
	subheaders: [[{ accessorKey: 'h_name' }]],
	table: [
		{ accessorKey: 'lay_class', header: () => 'Class' },
		{ accessorKey: 'h_name', header: () => 'Name' },
		{ accessorKey: 'h_id', header: () => 'ID' },
		{ accessorKey: 'epi_name', header: () => 'Plan' },
		{ accessorKey: 'sig', header: () => 'Significance' },
		{ accessorKey: 'legis_ref_clause', header: () => 'Clause' },
		{ accessorKey: 'pco_ref_key', header: () => 'Reference' }
	]
};

const zoningAttributes: AttributesLayoutHelper = {
	title: { accessorKey: 'lay_class' },
	subheaders: [[{ accessorKey: 'sym_code' }]],
	table: [
		{ accessorKey: 'sym_code', header: () => 'Code' },
		{ accessorKey: 'lay_class', header: () => 'Class' },
		{ accessorKey: 'epi_name', header: () => 'Plan' },
		{ accessorKey: 'lay_name', header: () => 'Layer' },
		{ accessorKey: 'amendment', header: () => 'Amendment' }
	]
};

// Legend-grid column labels for the crash module: one per severity band.
const FSI_COLUMN = 'Fatal and Serious Injury Crashes';
const OTHER_COLUMN = 'Other Injury Crashes';

const CRASH_SEVERITIES = {
	'FSI Crashes': [FSI_COLUMN, 4, ['Fatal', 'Serious Injury']],
	'Other Injury Crashes': [OTHER_COLUMN, 3, ['Moderate Injury', 'Minor/Other Injury']]
} as const;

const CRASH_ROAD_USERS = {
	All: null,
	Pedestrian: 'involves_pedestrian',
	Cyclist: 'involves_cyclist',
	Motorcycle: 'involves_motorcycle',
	'Heavy Vehicle': 'involves_heavy_vehicle'
} as const;

type CrashLayerId = `${keyof typeof CRASH_SEVERITIES} - ${keyof typeof CRASH_ROAD_USERS}`;

// One layer per severity band × road-user involvement, all drawing the 'Road Crash Data' feature and differing only by filter.
function crashLayers(): Record<CrashLayerId, LayerEntry> {
	const layers = {} as Record<CrashLayerId, LayerEntry>;
	for (const [severity, [column, zIndex, degrees]] of Object.entries(CRASH_SEVERITIES)) {
		const band = ['any', ...degrees.map((d) => ['==', 'degree_of_crash___detailed', d])];
		for (const [row, attr] of Object.entries(CRASH_ROAD_USERS)) {
			layers[`${severity} - ${row}` as CrashLayerId] = {
				featureTitle: 'Road Crash Data',
				map: {
					style: { ...styleCrashes, filter: attr ? ['all', band, ['==', attr, 'Y']] : band },
					zIndex
				},
				attributes: crashAttributes,
				legend: { grid: { column, row } }
			};
		}
	}
	layers['FSI Crashes - All'].legend!.initiallyVisible = true;
	return layers;
}

// Attributes-panel layout shared by the 'Speed Zones' and 'School Zones'
// entries — both render the one 'Speed Zones' root feature.
const speedZoneAttributes: AttributesLayoutHelper = {
	title: { accessorKey: 'sz_speed', cell: (info) => `Speed: ${info}` },
	subheaders: [[{ accessorKey: 'sz_type' }], [{ accessorKey: 'sz_status' }]],
	table: [
		{ accessorKey: 'sz_speed', header: () => 'Speed' },
		{ accessorKey: 'sz_type', header: () => 'Type' },
		{ accessorKey: 'sz_status', header: () => 'Status' },
		{ accessorKey: 'sz_direction', header: () => 'Direction' }
	]
};

// Attributes-panel layout shared by the speed-zone-extent layers — they all
// render the one 'Speed Zone Extents' root feature, so a selected extent looks
// identical whichever legend row drew it.
const speedZoneExtentAttributes: AttributesLayoutHelper = {
	title: {
		accessorKey: 'indicativespeedlimit',
		cell: (info) => `Indicative speed limit: ${info} (km/h)`
	},
	subheaders: [[{ accessorKey: 'dorasclass' }]],
	table: [
		{ accessorKey: 'query_geom_id', header: () => 'ID' },
		{ accessorKey: 'assessment', header: () => 'Assessment' },
		{ accessorKey: 'streetenvironment', header: () => 'Street environment' },
		{ accessorKey: 'placecontext', header: () => 'Place context' },
		{ accessorKey: 'dorasclass', header: () => 'DORAS class' },
		{ accessorKey: 'indicativespeedlimit', header: () => 'Indicative speed limit (km/h)' }
	]
};

const seifaAttributes = {
	title: { accessorKey: 'irsad_decile', cell: (info) => `ISRAD decile: ${info}` },
	body: [
		{ accessorKey: 'sa1_code_2021', header: () => 'SA1 code 2021' },
		{ accessorKey: 'sa2_code_2021', header: () => 'SA2 code 2021' }
	],
	table: [
		{ accessorKey: 'irsad_decile', header: () => `ISRAD decile` },
		{ accessorKey: 'irsd_score', header: () => 'IRSD Score' },
		{ accessorKey: 'ier_score', header: () => 'IER Score' },
		{ accessorKey: 'ieo_score', header: () => 'IEO Score' }
	]
} satisfies AttributesLayoutHelper;

// Speed-zone-extent layers all draw the 'Speed Zone Extents' feature (one
// shared vector source), differing only by the style attribute they colour on.
function speedZoneExtentLayer(
	style: LayerEntry['map']['style'],
	zIndex: number,
	extra?: Partial<LayerEntry>
): LayerEntry {
	return {
		featureTitle: 'Speed Zone Extents',
		map: { style, zIndex },
		attributes: speedZoneExtentAttributes,
		...extra
	};
}

// Planned and proposed infrastructure share one style and layout, differing only by feature.
function infrastructureLayer(tabId: string, label: string): LayerEntry {
	return {
		map: { style: styleInfrastructure, zIndex: 4 },
		optional: true,
		attributes: {
			title: { accessorKey: 'infrastructuretype' },
			table: [
				{ accessorKey: 'infrastructuretype', header: () => 'Type' },
				{ accessorKey: 'focusarea', header: () => 'Focus area' }
			]
		},
		table: { tabId, label, rowLabel: 'point' }
	};
}

// State and regional roads share a layout, differing only by style and tab.
function roadLayer(style: LayerEntry['map']['style'], tabId: string, label: string): LayerEntry {
	return {
		map: { style, zIndex: 4 },
		attributes: {
			title: { accessorKey: 'road_name' },
			table: [
				{ accessorKey: 'road_name', header: () => 'Road name' },
				{ accessorKey: 'road_number', header: () => 'Road number' },
				{ accessorKey: 'ne_unique', header: () => 'NE Unique' }
			]
		},
		table: { tabId, label, rowLabel: 'road' }
	};
}

// Community and recreation POIs share a layout; `fallbackLabel` names the style's icon fallback.
function poiLayer(
	style: LayerEntry['map']['style'],
	fallbackLabel: string,
	tabId: string,
	label: string
): LayerEntry {
	return {
		map: { style, zIndex: 4 },
		legend: { symbology: { fallbackLabel } },
		attributes: {
			title: { accessorKey: 'poiname' },
			table: [
				{ accessorKey: 'poiname', header: () => 'Name' },
				{ accessorKey: 'poitype', header: () => 'Type' }
			]
		},
		table: { tabId, label, rowLabel: 'point' }
	};
}

// Maps the raw `functional_road_class` code to its NSW hierarchy label; the
// code passes through unchanged when it isn't one we recognise.
const functionalRoadClassLabels: Record<number, string> = {
	10: 'Motorway',
	20: 'Major Road of High Importance',
	30: 'Other Major Road',
	40: 'Secondary Road',
	51: 'Local Connecting Road',
	52: 'Local Road of High Importance',
	60: 'Local Road of Moderate Importance',
	70: 'Local Road of Minor Importance',
	80: 'Other Road'
};

// FSI layers sit one z-level above the other-injury layers so the more severe
// crashes always render on top when both severity bands are enabled.
const NSW_RLGSP_MODULES = {
	'Speed Zone Extents': {
		label: 'Speed Zone Extents',
		// Module-level tab spanning both layers — they share a root feature, so
		// rows dedupe by feature id (the risk-module idiom).
		table: {
			tabId: 'SpeedZoneExtents',
			label: 'Speed Zone Extents',
			rowLabel: 'speed zone extent',
			columns: toColumns(speedZoneExtentAttributes.table!.slice(1))
		},
		layers: {
			'Indicative Speed': speedZoneExtentLayer(styleSpeedZoneExtentsSpeed, 3, {
				// Raw style keys are bare numbers ("10", "20", …).
				legend: { symbology: { formatLabel: (key) => `${key} km/h` } }
			}),
			'DORAS Class': speedZoneExtentLayer(styleSpeedZoneExtentsDoras, 3, {
				// Match keys are lowercased (the style downcases the attribute to
				// absorb source casing drift), so restore sentence case for display.
				legend: {
					symbology: { formatLabel: (key) => String(key).replace(/^./, (c) => c.toUpperCase()) }
				}
			}),
			'Planned infrastructure': infrastructureLayer(
				'PlannedInfrastructure',
				'Planned infrastructure'
			),
			'Proposed infrastructure': infrastructureLayer(
				'ProposedInfrastructure',
				'Proposed infrastructure'
			),
			'Historic speeds': {
				map: { style: styleHistoricSpeeds, zIndex: 4 },
				optional: true,
				attributes: {
					title: { accessorKey: 'speed85', cell: (info) => `85th percentile speed: ${info} km/h` },
					subheaders: [[{ accessorKey: 'location' }]],
					table: [
						{ accessorKey: 'location', header: () => 'Location' },
						{ accessorKey: 'date', header: () => 'Date' },
						{ accessorKey: 'method', header: () => 'Method' },
						{ accessorKey: 'speed85', header: () => '85th percentile speed' },
						{ accessorKey: 'ave_speed', header: () => 'Average speed' }
					]
				},
				table: { tabId: 'HistoricSpeeds', label: 'Historic speeds', rowLabel: 'speed' }
			}
		}
	},
	'Road Crash Data': {
		label: 'Road Crash Data 2020-2024',
		table: {
			tabId: 'RoadCrashes',
			label: 'Road Crashes',
			rowLabel: 'crash point',
			columns: [
				{ accessorKey: 'degree_of_crash___detailed', header: () => 'Degree of crash' },
				{ accessorKey: 'year_of_crash', header: () => 'Year' },
				{ accessorKey: 'month_of_crash', header: () => 'Month' },
				{ accessorKey: 'day_of_week_of_crash', header: () => 'Day of week' },
				{ accessorKey: 'two_hour_intervals', header: () => 'Time (2hr. window)' },
				{ accessorKey: 'type_of_location', header: () => 'Location type' },
				{ accessorKey: 'no__killed', header: () => 'Killed' },
				{ accessorKey: 'no__seriously_injured', header: () => 'Seriously injured' },
				{ accessorKey: 'no__moderately_injured', header: () => 'Moderately injured' },
				{ accessorKey: 'no__minor_other_injured', header: () => 'Minor/other injured' },
				{ accessorKey: 'involves_pedestrian', header: () => 'Pedestrian' },
				{ accessorKey: 'involves_cyclist', header: () => 'Cyclist' },
				{ accessorKey: 'involves_motorcycle', header: () => 'Motorcycle' },
				{ accessorKey: 'involves_heavy_vehicle', header: () => 'Heavy vehicle' }
			]
		},
		layers: crashLayers()
	},
	'Operating Speed': {
		label: 'Operating Speed',
		layers: {
			'Operating Speed': {
				map: { style: styleOperatingSpeed, zIndex: 4 },
				// One label per `step` output in the style (12 bins + auto "No data").
				legend: {
					initiallyVisible: true,
					symbology: {
						stepLabels: [
							'1-10 km/h',
							'11-20 km/h',
							'21-30 km/h',
							'31-40 km/h',
							'41-50 km/h',
							'51-60 km/h',
							'61-70 km/h',
							'71-80 km/h',
							'81-90 km/h',
							'91-100 km/h',
							'101-110 km/h',
							'Over 110 km/h'
						]
					}
				},
				attributes: {
					title: { accessorKey: 'name' },
					subheaders: [
						[
							{
								accessorKey: 'average_freeflow_speed_band',
								cell: (info) => `Avg. freeflow speed: ${info} km/h`
							}
						]
					],
					table: [
						{ accessorKey: 'name', header: () => 'Name' },
						{
							accessorKey: 'average_freeflow_speed_band',
							header: () => `Avg. freeflow speed (km/h)`
						},
						{
							accessorKey: 'functional_road_class',
							header: () => 'Functional road class',
							cell: (info) => functionalRoadClassLabels[Number(info)] ?? info
						}
					]
				},
				table: {
					tabId: 'OperatingSpeed',
					label: 'Operating Speed',
					rowLabel: 'operating speed',
					columns: [
						{ accessorKey: 'name', header: () => 'Road name' },
						{
							accessorKey: 'average_freeflow_speed_band',
							header: () => 'Avg. freeflow speed (km/h)'
						},
						{
							accessorKey: 'functional_road_class',
							header: () => 'Functional road class',
							cell: (info) => functionalRoadClassLabels[Number(info.getValue())] ?? info.getValue()
						}
					]
				}
			}
		}
	},
	'Speed Zones': {
		label: 'Speed Zones',
		layers: {
			// Listed before 'Speed Zones' so the wide school band draws beneath the
			// thin zone lines — equal zIndex keeps table order.
			'School Zones': {
				featureTitle: 'Speed Zones',
				map: { style: styleSpeedZonesSchools, zIndex: 4 },
				// Restyles school-type zones in the primary's own speed classes, so it
				// contributes no symbology items of its own.
				legend: { partOf: 'Speed Zones', hideSymbology: true },
				attributes: speedZoneAttributes
			},
			'Speed Zones': {
				map: { style: styleSpeedZones, zIndex: 4 },
				attributes: speedZoneAttributes,
				table: {
					tabId: 'SpeedZones',
					label: 'Speed Zones',
					rowLabel: 'speed zone'
				}
			}
		}
	},
	'Risk Ratings': {
		label: 'Risk Ratings',
		layers: {
			AusRAP: {
				map: { style: styleRiskAusrap, zIndex: 4 },
				attributes: {
					title: { accessorKey: 'veh_str_rating_smoothed', cell: (info) => `${info} star` },
					body: [
						{ accessorKey: 'road_type', header: () => 'Road type' },
						{ accessorKey: 'survey_date', header: () => 'Survey date' }
					],
					table: [
						{ accessorKey: 'road_name', header: () => 'Road name' },
						{ accessorKey: 'ausrap_section_id', header: () => 'AusRAP section ID' }
					]
				},
				table: {
					tabId: 'AusRAP',
					label: 'AusRAP',
					rowLabel: 'segment',
					columns: [
						{ accessorKey: 'road_name', header: () => 'Road name' },
						{
							accessorKey: 'veh_str_rating_smoothed',
							header: () => 'Star rating',
							cell: (info) => `${info.getValue()} star`
						},
						{ accessorKey: 'road_type', header: () => 'Road type' },
						{ accessorKey: 'survey_date', header: () => 'Survey date' },
						{ accessorKey: 'ausrap_section_id', header: () => 'AusRAP section ID' }
					]
				}
			}
		}
	},
	'Road Network': {
		label: 'Road Network',
		layers: {
			'State Roads': roadLayer(styleRoadNetworkState, 'StateRoads', 'State Roads'),
			'Regional Roads': roadLayer(styleRoadNetworkRegional, 'RegionalRoads', 'Regional Roads'),
			Cycleways: {
				map: { style: styleRoadNetworkCycleways, zIndex: 4 },
				attributes: {
					title: { accessorKey: 'name' },
					subheaders: [[{ accessorKey: 'facility_type' }]],
					table: [
						{ accessorKey: 'name', header: () => 'Facility name' },
						{ accessorKey: 'facility_type', header: () => 'Facility type' },
						{ accessorKey: 'width', header: () => 'Width' },
						{ accessorKey: 'maintainer', header: () => 'Maintainer' }
					]
				},
				table: {
					tabId: 'Cycleways',
					label: 'Cycleways',
					rowLabel: 'cycleway'
				}
			},
			'Bus Routes': {
				map: { style: styleRoadNetworkBusRoutes, zIndex: 4 },
				attributes: {
					title: { accessorKey: 'route' },
					subheaders: [[{ accessorKey: 'route_name' }]],
					table: [
						{ accessorKey: 'route', header: () => 'Route' },
						{ accessorKey: 'route_name', header: () => 'Route name' },
						{
							accessorKey: 'route_distance',
							header: () => 'Distance (km)',
							cell: (info) => Math.round((info as number) / 1000)
						}
					]
				},
				table: {
					tabId: 'BusRoutes',
					label: 'Bus routes',
					rowLabel: 'bus route',
					columns: [
						{ accessorKey: 'route', header: () => 'Route' },
						{ accessorKey: 'route_name', header: () => 'Route name' },
						{
							accessorKey: 'route_distance',
							header: () => 'Route distance (km)',
							cell: (info) => Math.round((info.getValue() as number) / 1000)
						}
					]
				}
			},
			'Level Crossings': {
				map: { style: styleRoadNetworkLevelCrossings, zIndex: 4 },
				// The style's icon fallback is the boom-gates symbol.
				legend: { symbology: { fallbackLabel: 'Boom Gates' } },
				attributes: {
					title: { accessorKey: 'control_category' }
				},
				table: {
					tabId: 'LevelCrossings',
					label: 'Level crossings',
					rowLabel: 'level crossing',
					columns: [{ accessorKey: 'control_category', header: () => 'Control category' }]
				}
			}
		}
	},
	'Points of Interest': {
		label: 'Points of Interest',
		layers: {
			'School locations': {
				map: { style: stylePoiSchools, zIndex: 4 },
				legend: { symbology: { fallbackLabel: 'Other' } },
				attributes: {
					title: { accessorKey: 'scl_name' },
					table: [
						{ accessorKey: 'scl_name', header: () => 'Name' },
						{ accessorKey: 'sll_desc', header: () => 'Description' },
						{ accessorKey: 'scl_address1', header: () => 'Address' }
					]
				},
				table: {
					tabId: 'SchoolLocations',
					label: 'School locations',
					rowLabel: 'school'
				}
			},
			// Community's icon fallback covers every non-hospital facility; recreation's is the sporting-facility symbol.
			'Points of Interest (Community)': poiLayer(
				stylePoiCommunity,
				'Other Facility',
				'PoiCommunity',
				'POIs (Community)'
			),
			'Points of Interest (Recreation)': poiLayer(
				stylePoiRecreation,
				'Sporting Facility',
				'PoiRecreation',
				'POIs (Recreation)'
			)
		}
	},
	// Planning polygons sit below every other data layer (zIndex 0/1); heritage
	// overlays draw above the broad-scale zoning colours. Each conceptual layer
	// is a fill (primary legend row) plus a `partOf` line layer for boundaries
	// the fill symbology can't express.
	Planning: {
		label: 'Planning',
		layers: {
			Heritage: {
				map: { style: stylePlanningHeritageFill, zIndex: 2 },
				attributes: heritageAttributes,
				table: {
					tabId: 'PlanningHeritage',
					label: 'Heritage',
					rowLabel: 'heritage area'
				}
			},
			'Heritage (outline)': {
				featureTitle: 'Heritage',
				map: { style: stylePlanningHeritageLine, zIndex: 2 },
				legend: { partOf: 'Heritage' },
				attributes: heritageAttributes
			},
			'Land Zoning': {
				map: { style: stylePlanningZoningFill, zIndex: 1 },
				// Zoning fill matches on a composite `sym_code|lay_class` key.
				legend: {
					symbology: {
						formatLabel: (key) => {
							const [code, name] = String(key).split('|');
							return name ? `${code} – ${name}` : String(key);
						}
					}
				},
				attributes: zoningAttributes,
				table: {
					tabId: 'PlanningZoning',
					label: 'Land Zoning',
					rowLabel: 'zoning area'
				}
			},
			'Land Zoning (outline)': {
				featureTitle: 'Land Zoning',
				map: { style: stylePlanningZoningLine, zIndex: 2 },
				legend: {
					partOf: 'Land Zoning',
					symbology: {
						formatLabel: (key) => (key === 'DM' ? 'Deferred Matter boundary' : String(key)),
						fallbackLabel: 'Zone boundary'
					}
				},
				attributes: zoningAttributes
			}
		}
	},
	'SEIFA index': {
		label: 'SEIFA index',
		layers: {
			'SEIFA index': {
				map: { style: styleSeifa, zIndex: 1 },
				// One label per `step` output — IRSAD deciles.
				legend: {
					symbology: {
						stepLabels: [
							'Decile 1 (most disadvantaged)',
							'Decile 2',
							'Decile 3',
							'Decile 4',
							'Decile 5',
							'Decile 6',
							'Decile 7',
							'Decile 8',
							'Decile 9',
							'Decile 10 (least disadvantaged)'
						]
					}
				},
				attributes: seifaAttributes,
				table: {
					tabId: 'SEIFA',
					label: 'SEIFA index',
					rowLabel: 'statistical area',
					columns: toColumns([...seifaAttributes.body, ...seifaAttributes.table])
				}
			}
		}
	},
	'LGA Areas': {
		label: 'LGA Areas',
		layers: {
			'LGA Areas': {
				map: { style: styleLgaAreas, zIndex: 0, selectable: false },
				legend: { initiallyVisible: true },
				attributes: {
					title: { accessorKey: 'lganame_appended' }
				}
			}
		}
	}
} satisfies Record<string, ModuleEntry>;

type ModuleTable = typeof NSW_RLGSP_MODULES;

/** module-id union. Fulfils the generic `<M extends string>` slot. */
export type NswRlgspModule = keyof ModuleTable & string;

/** `module → layerId → ILayerSource | null` envelope consumed by Map/Table. */
export type INswRlgspSystemLayers = {
	[M in NswRlgspModule]: {
		[L in keyof ModuleTable[M]['layers']]: ILayerSource | null;
	};
};

// ---------------------------------------------------------------------------
// Derived AppConfig pieces — never edited when layers change.
// ---------------------------------------------------------------------------

// The wide view of the table, for iteration: `Object.entries` walks need the
// index-signature shape, not the literal keys.
export const moduleTable: Record<string, ModuleEntry> = NSW_RLGSP_MODULES;

export const attributesColumns = attributesColumnsOf(moduleTable);

export const tableTabs = tableTabsOf<INswRlgspSystemLayers>(moduleTable);
